var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { MultiColumnList, Select, Button, AccordionSet, Accordion, Tooltip, Layout, Headline, } from '@folio/stripes/components';
import { RecordType, ProposalType, WORKFLOW_TABS, } from '../types/api';
import SearchLayout from '../components/SearchLayout';
import { TableLoadingSkeleton, TableRefetchOverlay, TableErrorState, } from '../components/TableStates';
import useProposalList from '../hooks/useProposalList';
import useAllowedProposalActions from '../hooks/useAllowedProposalActions';
import useAuthorizationContext from '../hooks/useAuthorizationContext';
import useOrganizationNames from '../hooks/useOrganizationNames';
import ProposalDetailView from '../components/ProposalDetail/ProposalDetail';
import AuthorityWorkspaceNavigation from '../components/AuthorityWorkspaceNavigation';
import { AuthorityTypeLabel } from '../components/AuthorityTypeCard';
import { AuthorRepliedBadge } from '../components/ReviewThread';
import css from './ProposalsList.css';
var MAX_HEADING_LENGTH = 80;
/** The heading, truncated to {@link MAX_HEADING_LENGTH} with the full text in a tooltip. */
var headingCell = function (record) {
    if (record.headingValue.length <= MAX_HEADING_LENGTH) {
        return record.headingValue;
    }
    var truncated = "".concat(record.headingValue.slice(0, MAX_HEADING_LENGTH), "...");
    return (React.createElement(Tooltip, { text: record.headingValue, id: "heading-tooltip-".concat(record.id) }, function (_a) {
        var ref = _a.ref, ariaIds = _a.ariaIds;
        return (React.createElement("span", { ref: ref, "aria-labelledby": ariaIds.text }, truncated));
    }));
};
/**
 * Tabs that use submittedAt as the date column.
 * All other tabs use updatedAt.
 */
var SUBMITTED_DATE_TABS = [
    'PENDING_NEW',
    'PENDING_UPDATE',
    'REJECTED',
    'APPROVED',
];
/**
 * Tabs that have a subtext in their empty state.
 */
var TABS_WITH_SUBTEXT = [
    'DRAFT',
    'PENDING_NEW',
    'PENDING_UPDATE',
    'CHANGES_REQUESTED',
];
/**
 * Tabs where the proposalType filter is hidden (tab already implies it).
 */
var TABS_WITH_FIXED_PROPOSAL_TYPE = [
    'PENDING_NEW',
    'PENDING_UPDATE',
];
var ProposalsList = function (_a) {
    var _b;
    var match = _a.match, selectedId = _a.selectedId, _c = _a.allowedTabs, allowedTabs = _c === void 0 ? WORKFLOW_TABS : _c, defaultTab = _a.defaultTab, forcedOwnership = _a.forcedOwnership, _d = _a.paneTitleId, paneTitleId = _d === void 0 ? 'ui-czna-authorities.proposals.title' : _d;
    var intl = useIntl();
    var history = useHistory();
    var list = useProposalList({ allowedTabs: allowedTabs, defaultTab: defaultTab, forcedOwnership: forcedOwnership });
    var context = useAuthorizationContext().context;
    // CZNA-598 — "may this user start any proposal at all", answered by the backend.
    var canCreateProposal = useAllowedProposalActions().canCreateProposal;
    var getOrganizationName = useOrganizationNames().getOrganizationName;
    var myUserId = (_b = context === null || context === void 0 ? void 0 : context.userId) !== null && _b !== void 0 ? _b : null;
    var isMyProposal = useCallback(function (record) { return !!myUserId && record.createdBy === myUserId; }, [myUserId]);
    var handleRowClick = useCallback(function (_e, row) {
        history.push("".concat(match.path, "/").concat(row.id));
    }, [match.path, history]);
    /**
     * Returns the appropriate date string for a proposal based on the active tab.
     */
    var getDateValue = useCallback(function (record) {
        var useSubmittedAt = SUBMITTED_DATE_TABS.includes(list.activeTab);
        var dateStr = useSubmittedAt ? record.submittedAt : record.updatedAt;
        if (!dateStr)
            return '';
        var date = intl.formatDate(dateStr, { day: '2-digit', month: '2-digit', year: 'numeric' });
        var time = intl.formatTime(dateStr, { hour: '2-digit', minute: '2-digit' });
        return "".concat(date, " ").concat(time);
    }, [list.activeTab, intl]);
    var resultsFormatter = {
        authorityId: function (record) { return record.authorityId; },
        // CZNA-949 — the supervisor sees on the row that the author has answered a return.
        headingValue: function (record) { return (record.authorReplied
            ? React.createElement(React.Fragment, null,
                headingCell(record),
                React.createElement(AuthorRepliedBadge, null))
            : headingCell(record)); },
        type: function (record) { return React.createElement(AuthorityTypeLabel, { type: record.recordType }); },
        proposalType: function (record) {
            return intl.formatMessage({ id: "ui-czna-authorities.proposals.proposalType.".concat(record.proposalType) });
        },
        // A proposal with no organization renders as a dash; an organization we could not
        // resolve renders as its raw UUID, so an unreadable column looks broken instead of
        // looking empty (the two failures need different fixes).
        organization: function (record) {
            var _a;
            if (!record.organizationId)
                return '—';
            return (_a = getOrganizationName(record.organizationId)) !== null && _a !== void 0 ? _a : record.organizationId;
        },
        date: function (record) { return getDateValue(record); },
    };
    var visibleColumns = useMemo(function () {
        return ['authorityId', 'headingValue', 'type', 'proposalType', 'organization', 'date'];
    }, []);
    /** MCL exposes only a cell hook, so every cell receives the same beige row tint. */
    var getCellClass = useCallback(function (defaultClasses, record) {
        if (record.id === selectedId)
            return defaultClasses;
        var classes = [defaultClasses];
        if (isMyProposal(record))
            classes.push(css.ownProposalCell);
        return classes.join(' ');
    }, [isMyProposal, selectedId]);
    var proposalTypeOptions = __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.proposalType.all' }) }
    ], Object.values(ProposalType).map(function (pt) { return ({
        value: pt,
        label: intl.formatMessage({ id: "ui-czna-authorities.proposals.proposalType.".concat(pt) }),
    }); }), true);
    var typeOptions = __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.type.all' }) }
    ], Object.values(RecordType).map(function (type) { return ({
        value: type,
        label: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(type) }),
    }); }), true);
    /**
     * CZNA-593 — Vše / Jen moje / Moje instituce. "Moje instituce" is dropped for a user
     * who belongs to no organization: the option would have nothing to mean and would just
     * empty the list. It is NOT dropped for national-level users — membership and permission
     * scope are different things, and an NK cataloguer legitimately wants NK's own rows.
     */
    var ownershipOptions = __spreadArray([
        {
            value: 'ALL',
            label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.ownership.ALL' }),
        },
        {
            value: 'MINE',
            label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.ownership.MINE' }),
        }
    ], (list.canFilterByOrganization ? [{
            value: 'ORGANIZATION',
            label: intl.formatMessage({
                id: 'ui-czna-authorities.proposals.filter.ownership.ORGANIZATION',
            }),
        }] : []), true);
    var isProposalTypeFilterHidden = TABS_WITH_FIXED_PROPOSAL_TYPE.includes(list.activeTab);
    var filterContent = (React.createElement(AccordionSet, null,
        !forcedOwnership && (React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.ownership' }), separator: false },
            React.createElement(Select
            // The Accordion heading is not the Select's accessible name, so screen readers
            // (and the Layer-1 locators) get nothing without this. Stripes renders it as an
            // sr-only <label> linked via aria-labelledby.
            , { "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.ownership' }), value: list.ownershipFilter, dataOptions: ownershipOptions, onChange: function (e) {
                    return list.handleOwnershipFilterChange(e.target.value);
                } }))),
        !isProposalTypeFilterHidden && (React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.proposalType' }), separator: false },
            React.createElement(Select, { value: list.proposalTypeFilter || '', dataOptions: proposalTypeOptions, onChange: function (e) {
                    return list.handleProposalTypeFilterChange(e.target.value || null);
                } }))),
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.type' }), separator: false },
            React.createElement(Select, { value: list.typeFilter || '', dataOptions: typeOptions, onChange: function (e) {
                    return list.handleTypeFilterChange(e.target.value || null);
                } }))));
    var columnMapping = {
        authorityId: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.column.authorityId" }),
        headingValue: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.column.headingValue" }),
        type: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.column.type" }),
        proposalType: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.column.proposalType" }),
        organization: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.column.organization" }),
        date: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.column.date" }),
    };
    // `organization` is not sortable: the backend has no sortBy for it (SORT_COLUMN_MAP
    // in useProposalList), and sorting only the loaded page would be a lie about the rest.
    var nonInteractiveHeaders = ['authorityId', 'proposalType', 'organization'];
    var workflowTabs = (React.createElement("div", { style: { marginBottom: '8px' } }, allowedTabs.map(function (tab) {
        var _a, _b;
        // CZNA-595: no badge at all for zero, and none while the count is unknown.
        // Seven tabs each showing "0" is noise the eye has to filter; the badge exists
        // to point at where there IS work. The empty state inside a tab already says
        // "you have nothing here", and says it in words.
        var count = (_b = (_a = list.tabCounts) === null || _a === void 0 ? void 0 : _a[tab]) !== null && _b !== void 0 ? _b : 0;
        return (React.createElement(Button, { key: tab, buttonStyle: list.activeTab === tab ? 'primary' : 'default', onClick: function () { return list.handleTabChange(tab); }, fullWidth: true, style: { justifyContent: 'flex-start' } },
            React.createElement("span", { style: { display: 'flex', width: '100%', alignItems: 'center', gap: '8px' } },
                React.createElement("span", null,
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.tab.".concat(tab) })),
                count > 0 && (React.createElement("span", { style: { marginLeft: 'auto' } },
                    React.createElement("span", { "aria-hidden": "true", "data-testid": "proposal-tab-count-".concat(tab) }, count),
                    React.createElement("span", { className: "sr-only" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.tab.count", values: { count: count } })))))));
    })));
    var renderContent = function () {
        // CZNA-538 — before the empty branch: a failed query used to render as
        // "no proposals matching the criteria", i.e. as the user's filter being wrong.
        if (list.error && list.items.length === 0) {
            return React.createElement(TableErrorState, { error: list.error, onRetry: list.refetch, testId: "proposals-error" });
        }
        // `isLoading || isFetching`, not `isLoading` alone: react-query only reports
        // `isLoading` while a key has no cached data at all, so a re-run of an already
        // seen query with no rows fell into the empty branch while still in flight.
        if ((list.isLoading || list.isFetching) && list.items.length === 0) {
            return React.createElement(TableLoadingSkeleton, { testId: "proposals-loading", columns: [3, 9, 3, 3, 3] });
        }
        if (list.items.length === 0 && !list.isLoading && !list.isFetching) {
            if (list.isDefaultFilters) {
                var hasSubtext = TABS_WITH_SUBTEXT.includes(list.activeTab);
                // CZNA-591: gated on the same predicate as the "nový návrh" button in
                // lastMenu. It was not, so a user with no cataloguing role saw no button
                // in the header and a call to action in the middle of the pane — leading
                // to the very form the save would refuse.
                var hasCta = list.activeTab === 'DRAFT' && canCreateProposal;
                return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                    React.createElement(Layout, { className: "padding-all-gutter" },
                        React.createElement(Headline, { size: "medium", margin: "none" },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.empty.".concat(list.activeTab, ".heading") })),
                        hasSubtext && (React.createElement("p", { style: { color: '#666', marginTop: '8px' } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.empty.".concat(list.activeTab, ".subtext") }))),
                        hasCta && (React.createElement(Button, { buttonStyle: "primary", style: { marginTop: '16px' }, onClick: function () { return history.push('/czna-authorities/authorities/new'); } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.new" }))))));
            }
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement("p", { style: { color: '#666' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.noResults" })),
                    React.createElement(Button, { onClick: list.handleClearFilters, style: { marginTop: '16px' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.clearFilters" })))));
        }
        return (
        /* CZNA-538 — see AuthoritiesList: the overlay replaces MCL's own corner spinner. */
        React.createElement(TableRefetchOverlay, { active: list.isFetching, testId: "proposals-refetching" },
            React.createElement(MultiColumnList, { autosize: true, virtualize: true, contentData: list.items, 
                // CZNA-780 — MCL's PAGING total, never the count shown in the pane header:
                // `list.totalRecords` is null until the count lands, and a falsy totalCount
                // silently switches MCL to probing contentData for a hole (see useProposalList).
                totalCount: list.paginationTotalCount, 
                // CZNA-1191: that total is in page coordinates, so "next" is answered explicitly.
                pagingCanGoNext: list.paginationCanGoNext, pagingType: "prev-next", pagingOffset: list.pageOffset, pageAmount: list.pageSize, columnMapping: columnMapping, visibleColumns: visibleColumns, formatter: resultsFormatter, getCellClass: getCellClass, sortedColumn: list.sortColumn, sortDirection: list.sortDirection, onHeaderClick: list.handleSort, nonInteractiveHeaders: nonInteractiveHeaders, selectedRow: selectedId ? { id: selectedId } : undefined, onRowClick: handleRowClick, onNeedMoreData: function (_amount, offset) {
                    return list.fetchMoreData(offset);
                } })));
    };
    // CZNA-778 — the detail brings its own Pane, so its actions can sit in the header.
    var detailPane = selectedId ? (React.createElement(ProposalDetailView, { id: selectedId, onClose: function () { return history.push(match.path); }, paneProps: {
            defaultWidth: '44%',
            paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.title" }),
        } })) : undefined;
    var newProposalButton = canCreateProposal ? (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () { return history.push('/czna-authorities/authorities/new'); } },
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.new" }))) : undefined;
    var headerMenu = (React.createElement(React.Fragment, null, newProposalButton));
    return (React.createElement(SearchLayout, { list: list, searchPlaceholder: intl.formatMessage({ id: 'ui-czna-authorities.proposals.search.placeholder' }), paneTitle: React.createElement(FormattedMessage, { id: paneTitleId }), lastMenu: headerMenu, filterContent: filterContent, subNavigation: (React.createElement(React.Fragment, null,
            React.createElement(AuthorityWorkspaceNavigation, null),
            allowedTabs.length > 1 ? workflowTabs : null)), detailPane: detailPane }, renderContent()));
};
export default ProposalsList;
